/**
 * BILL-05 duration hints. The server sizes an upload's credit hold from the
 * client's `duration_secs`; these two helpers produce it.
 *
 *  - `elapsedSecs` — the mic path: wall-clock time since the recorder started.
 *    MediaRecorder WebM carries no duration header, so the timer is the only
 *    source for a fresh capture.
 *  - `readAudioDurationSecs` — the file-picker path: the browser's own metadata
 *    read. Resolves `undefined` (never rejects) when the length is unknown, so
 *    the upload simply goes without a hint and the server falls back to a
 *    balance-sized cap.
 */

/** Seconds elapsed since `startedAtMs`, or undefined when recording never started. */
export function elapsedSecs(startedAtMs: number | null, nowMs: number): number | undefined {
  if (startedAtMs === null) return undefined;
  return (nowMs - startedAtMs) / 1000;
}

/**
 * Reads the audio duration from the file's metadata. Only a finite, positive
 * duration resolves a number: MediaRecorder WebM reports `Infinity`, an
 * undecodable file reports `NaN` or fires `error`, and a browser that never
 * answers is cut off after `timeoutMs`. The object URL is revoked on every path.
 */
export function readAudioDurationSecs(file: Blob, timeoutMs = 3000): Promise<number | undefined> {
  return new Promise((resolve) => {
    let url: string;
    let audio: HTMLAudioElement;
    try {
      if (typeof URL.createObjectURL !== 'function') {
        resolve(undefined);
        return;
      }
      audio = document.createElement('audio');
      url = URL.createObjectURL(file);
    } catch {
      resolve(undefined);
      return;
    }

    let done = false;
    const finish = (value: number | undefined) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('error', onError);
      try {
        audio.removeAttribute('src');
        audio.load();
      } catch {
        // Detaching is best-effort; the revoke below is what frees the bytes.
      }
      try {
        URL.revokeObjectURL(url);
      } catch {
        // Environments without revokeObjectURL hold nothing to free.
      }
      resolve(value);
    };
    const onMeta = () => {
      const d = audio.duration;
      finish(Number.isFinite(d) && d > 0 ? d : undefined);
    };
    const onError = () => finish(undefined);
    const timer = setTimeout(() => finish(undefined), timeoutMs);

    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('error', onError);
    audio.preload = 'metadata';
    audio.src = url;
  });
}
